import type { FC } from 'react';

/**
 * UVP Logo Design System
 * Vercel-inspired geometric logo concepts
 */

/**
 * Variant 1: Shield Checkmark
 * Shield shape with integrated checkmark - represents verification/security
 */
export const ShieldCheckLogo: FC<{ size?: number }> = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path
      d="M16 2L4 7V14C4 21.732 9.27 28.456 16 30C22.73 28.456 28 21.732 28 14V7L16 2Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M10 16L14 20L22 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Variant 2: Hexagon Network
 * Connected hexagons representing unification/network
 */
export const HexNetworkLogo: FC<{ size?: number }> = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path
      d="M16 4L8 8V16L16 20L24 16V8L16 4Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M8 20L16 24L24 20"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M16 20V24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="16" cy="12" r="2" fill="currentColor" />
    <circle cx="10" cy="10" r="1.5" fill="currentColor" />
    <circle cx="22" cy="10" r="1.5" fill="currentColor" />
  </svg>
);

/**
 * Variant 3: Unified Shield
 * Combined shield and hexagon shape with verification mark
 */
export const UnifiedShieldLogo: FC<{ size?: number }> = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path
      d="M16 3L5 8.5V15C5 22 10.5 28.5 16 30.5C21.5 28.5 27 22 27 15V8.5L16 3Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M11 15L15 19L21 13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 8V12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      opacity="0.5"
    />
    <path
      d="M16 12L22 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      opacity="0.5"
    />
    <path
      d="M16 12L10 9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      opacity="0.5"
    />
  </svg>
);

/**
 * Variant 4: Minimal U
 * Stylized U with verification dot - simple and modern
 */
export const MinimalULogo: FC<{ size?: number }> = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path
      d="M8 8V18C8 21.3137 10.6863 24 14 24H18C21.3137 24 24 21.3137 24 18V8"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <circle cx="16" cy="6" r="2" fill="currentColor" />
  </svg>
);

/**
 * UVP Logo - Recommended: Hexagon with internal structure
 * Best representing technical/unified/verification concepts
 */
export const UVPLogo: FC<{ size?: number }> = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    {/* Main hexagon outline */}
    <path
      d="M16 3L6 9V19L16 25L26 19V9L16 3Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      fill="none"
    />
    {/* Internal structure - represents unified nodes */}
    <path d="M16 3V13" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
    <path
      d="M16 13L22 9"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      opacity="0.3"
    />
    <path
      d="M16 13L10 9"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      opacity="0.3"
    />
    {/* Central verification checkmark */}
    <path
      d="M12 15L15 18L20 13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Bottom nodes */}
    <circle cx="16" cy="22" r="1.5" fill="currentColor" />
    <circle cx="10" cy="19" r="1" fill="currentColor" />
    <circle cx="22" cy="19" r="1" fill="currentColor" />
  </svg>
);

const LogoShowcase: FC = () => (
  <div style={{ padding: 40, background: '#ffffff' }}>
    <h2 style={{ fontFamily: 'Geist, sans-serif', marginBottom: 24 }}>UVP Logo Options</h2>

    <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap' }}>
      <div style={{ textAlign: 'center' }}>
        <ShieldCheckLogo size={48} />
        <p style={{ marginTop: 8, fontFamily: 'Geist, sans-serif', fontSize: 12, color: '#666' }}>
          Shield Check
        </p>
      </div>

      <div style={{ textAlign: 'center' }}>
        <HexNetworkLogo size={48} />
        <p style={{ marginTop: 8, fontFamily: 'Geist, sans-serif', fontSize: 12, color: '#666' }}>
          Hex Network
        </p>
      </div>

      <div style={{ textAlign: 'center' }}>
        <UnifiedShieldLogo size={48} />
        <p style={{ marginTop: 8, fontFamily: 'Geist, sans-serif', fontSize: 12, color: '#666' }}>
          Unified Shield
        </p>
      </div>

      <div style={{ textAlign: 'center' }}>
        <MinimalULogo size={48} />
        <p style={{ marginTop: 8, fontFamily: 'Geist, sans-serif', fontSize: 12, color: '#666' }}>
          Minimal U
        </p>
      </div>

      <div style={{ textAlign: 'center' }}>
        <UVPLogo size={48} />
        <p
          style={{
            marginTop: 8,
            fontFamily: 'Geist, sans-serif',
            fontSize: 12,
            color: '#666',
            fontWeight: 600,
          }}
        >
          UVP Logo ⭐
        </p>
      </div>
    </div>
  </div>
);

export default LogoShowcase;
